import { HTTP_INTERCEPTORS, HttpClient, HttpHeaders, HttpInterceptor, HttpResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';

import { IWebRequestOptions } from '../../../interfaces/web-request.interface';
import { AbreCuentasService } from '../../abre-cuentas/abre-cuentas.service';
import { CryptoVaultService } from '../../external/crypto-vault/crypto-vault.service';
import { ParametersService } from '../../parameters/parameters.service';
import { ChannelCommunicatorService } from '../channel-communicator/channel-communicator.service';
import { PlatformEndpointService } from '../endpoint/endpoint.service';
import { CipherCommunicatorService } from './cipher-communicator/cipher-communicator.service';
import { PlatformCommunicatorAuthService } from './platform-communicator.auth.service';
import { PlatformCommunicatorInterceptor } from './platform-communicator.interceptor';
import { RequestHeaderSettings } from './request.header.settings';

/**
 * Definición para que la clase sea visible desde root
 *
 * *Class definition to be seen from root*
 *
 * @type {Injectable}
 */
@Injectable({
  providedIn: 'root',
})

/**
 * Servicio responsable de la comunicación con el servicio backend de la aplicación
 *
 * *Service responsible for communicating with the Backend of the application.*
 *
 * @type {class}
 * @implements {HttpInterceptor}
 *
 * @author Hector Martinez (Accenture)
 */
export class PlatformCommunicatorService {

  /**
   * Definición de la variable parametrizedHeaders como HttpHeaders e indefinico
   *
   * *Defines parametrizedHeaders variable as HttpHeaders and undefined*
   *
   * @type {private}
   * @var {HttpHeaders} parametrizedHeaders
   */
  parametrizedHeaders: HttpHeaders | undefined = undefined;

  private communicatorInterceptor: PlatformCommunicatorInterceptor;

  /**
   * Constructor de la clase
   *
   * *Class Contstructor.*
   *
   * Inicializa los métodos para los modelos
   *
   * *Initialize method for models*
   *
   * @param {HttpClient} _http
   * @param {PlatformCommunicatorAuthService} authService
   */
  constructor(
    private _http: HttpClient,
    private authService: PlatformCommunicatorAuthService,
    private parametersService: ParametersService,
    private cipherCommunicator: CipherCommunicatorService,
    private abreCuentasService: AbreCuentasService,
    private cipherService: CryptoVaultService,
    private endpointService: PlatformEndpointService,
    private channelCommunicatorService: ChannelCommunicatorService,
    @Inject(HTTP_INTERCEPTORS) private interceptors: HttpInterceptor[],
  ) {
    this.interceptors.forEach(interceptor => {
      if (interceptor instanceof PlatformCommunicatorInterceptor) {
        this.communicatorInterceptor = interceptor;
      }
    });
  }

  /**
   * Función para procesar los headers
   *
   * *Function to process headers*
   *
   * @type {function}
   * @param {HttpHeaders} additionalHeaders
   * @returns {HttpHeaders} headers
   */
  private processHeaders(additionalHeaders: HttpHeaders | undefined, url: string, isPpid: boolean, appendSession: boolean): HttpHeaders {
    const requiredSpecificToken = (url === this.endpointService.URLS.GET_OTP || url === this.endpointService.URLS.VALIDATE_OTP)
     && this.abreCuentasService.getUserJemelii();
    console.log('///** REQUIRED TOKEN JEMELLI ' + requiredSpecificToken);
    const token = requiredSpecificToken ? this.authService.getSSOTokenForOTP().accessToken : this.authService.getSSOInformation().accessToken;

    const sessionId = this.authService.getSessionId();

    // const clientProduct = this.authService.clientProduct();

    let headers = RequestHeaderSettings.getHeaders(token, isPpid);

    // Colocarlo en el request service para esos 3
    if (url === this.endpointService.URLS.SEND_BACK_DOCUMENT_INFO ||
      url === this.endpointService.URLS.FRONT_DOCUMENT_INFO ||
      url === this.endpointService.URLS.FINGER_PRINTS_INFO ||
      url === this.endpointService.URLS.AGREEMENTS_OFFERS ||
      url === this.endpointService.URLS.GET_SIMULATION ||
      url === this.endpointService.URLS.TERMS_CONDITIONS) {
      headers = headers.append('use_cripto', 'false');
    }

    if (url !== this.endpointService.URLS.API_GATEWAY_SSO) {
      headers = headers.append('transaction_id', this.abreCuentasService.getTransactionId());
      headers = headers.append('channel-id', this.channelCommunicatorService.getChannelId().toString());
    }

    // TODO: se debe eliminar esta validacion cuando se implemente el nuevo apuntamiento al bff para listas restritivas
    if (url === this.endpointService.URLS.RESTRICTIVE_LIST ||
      url === this.endpointService.URLS.AGREEMENTS_LOAN
      ) {
      headers = headers.append('token-client-id',
        `${this.abreCuentasService.getDocumentInfo().document_number}_${this.abreCuentasService.getDocumentInfo().document_type}`);
    }

    if (this.endpointService.URLS.DOCUMENTARY_SUFFICIENCY && url.split('?')[0] === this.endpointService.URLS.DOCUMENTARY_SUFFICIENCY.split('?')[0]) {
      headers = headers.append('token_client_id',
        `${this.abreCuentasService.getDocumentInfo().document_number}_${this.abreCuentasService.getDocumentInfo().document_type}`);
    }
    // TODO- revisar en ambiente
    if (this.endpointService.URLS.LOAD_FILE && url.split('?')[0] === this.endpointService.URLS.LOAD_FILE.split('?')[0] && !this.abreCuentasService.getPostlogin()) {
      console.log(this.abreCuentasService.getDocumentInfo());
      headers = headers.append('token-user-id',
        `${this.abreCuentasService.getDocumentInfo().document_number}_${this.abreCuentasService.getDocumentInfo().document_type}`);
    }

    if (token && this.parametrizedHeaders !== undefined) {
      const keys = this.parametrizedHeaders.keys();
      headers = headers.append('operation_target', this.parametrizedHeaders.get('operation_target') as string);
      keys.map(chKey => {
        // tslint:disable-next-line: early-exit
        if (chKey === this.channelCommunicatorService.getChannelId() && this.parametrizedHeaders !== undefined) {
          const chHead: any = this.parametrizedHeaders.get(chKey);

          for (const key in chHead) {
            if (Object.prototype.hasOwnProperty.call(chHead, key)) {
              headers = headers.append(key, chHead[key] as string);
            }
          }
        }
      });
    }
    /* */
    // if (token && this.parametrizedHeaders !== undefined) {
    //   const keys = this.parametrizedHeaders.keys();

    //   for (const key of keys) {
    //     headers = headers.append(key, this.parametrizedHeaders.get(key) as string);
    //   }
    // }

    if (sessionId && appendSession) {
      headers = headers.append('session_id', sessionId as string);
    }

    if (additionalHeaders !== undefined) {
      let requestHeaders = additionalHeaders;

      const keys = headers.keys();

      for (const key of keys) {
        if (requestHeaders.get(key) === null) {
          requestHeaders = requestHeaders.append(key, headers.get(key) as string);
        }
      }

      headers = requestHeaders;
    }

    return headers;
  }

  /**
   * Función que inicializa los headers parámetrizados
   *
   * *Function that sets parametrized headers*
   *
   * @type {function}
   * @param {HttpHeaders} parametrizedHeaders
   */
  setParametrizedHeaders(parametrizedHeaders: HttpHeaders | undefined): void {
    this.parametrizedHeaders = parametrizedHeaders;
    this.abreCuentasService.setParametrizedHeaders(parametrizedHeaders);
  }

  /**
   * Solicitud que configura los parámetros de acuerdo un método de solicitud HTTP
   *
   * *Request that sets params up according to a specific HTTP request*
   *
   * @type {function}
   * @param {IWebRequestOptions} options
   * @param {boolean} encrypted
   * @returns {Promise<HttpResponse<any>>}
   */
  async request(options: IWebRequestOptions, encrypted?: boolean, isPpid = false, appendSession = true): Promise<HttpResponse<any>> {
    const headers = this.processHeaders(options.headers, options.url, isPpid, appendSession);
    options.headers = headers;
    if (encrypted && options.body) {
      await this.cipherService.encrypt(JSON.stringify(options.body)).then(data => {
        options.body = {
          data: {
            message: data,
          },
        };
      }, err => {
        return err;
      });
    }

    return new Promise<HttpResponse<any>>((resolve, reject) => {
      let fucntionParameters: any[];

      const url = this.getURLRequest(options.url, options.method);

      if (url !== options.url) {
        options.method = 'get';
      }

      if (options.method !== 'get' && options.method !== 'delete') {
        fucntionParameters = [
          options.url,
          options.body,
          { headers, params: options.params, observe: 'response' },
        ];
      } else {
        fucntionParameters = [
          url,
          { headers, params: options.params, observe: 'response' },
        ];
      }
      this._http[options.method].apply(this._http, fucntionParameters).toPromise().then(
        async (response: HttpResponse<any>) => {
          if (response.status === 200) {
            if (encrypted && (options.body || response.body != null) && response.body.data) {
              const decryptedResponse = await this.cipherService.decrypt(response.body.data.message);
              const decryptResponse: HttpResponse<any> = {
                body: JSON.parse(decryptedResponse),
                status: response.status,
                ok: true,
                headers: response.headers,
                statusText: response.statusText,
                type: response.type,
                url: response.url,
                clone: response.clone,
              };
              console.warn('request--', decryptResponse);
              resolve(decryptResponse);
            }
            resolve(response);
            return;
          }
          /*reject(response);
          return;*/
        }, (error: any) => {
        if (encrypted) {
          const decryptederror = this.cipherService.decrypt(error.error.data.message);
          console.warn('decryptederror', decryptederror);

          error.status === 422 && error.url.indexOf('/otp/') > 0 ? resolve(JSON.parse(decryptederror)) : reject(error);
          return;
        }
        reject(error);
        return;

      },
      ).catch((error: any) => {
        reject(error);
        return;
      });
    });
  }

  private getURLRequest(url: string, method: string): string {
    const backendURLMocked = this.parametersService.getParameter('url_mock_backend');
    if (backendURLMocked) {
      return this.communicatorInterceptor.getBackEndURLMocked(backendURLMocked, url, method);
    }

    return url;
  }
}
